import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@headlessui/react";
import { useAppSelector } from "../../../../app/hooks";
import { isMissingRequiredValue } from "../../../../app/requiredFields";
import { selectionChanged, textChanged } from "../../../../app/formChanges";
import AlertMessage, { AlertLevel } from "../../../../components/AlertMessage";
import { DistrictCreateEditModel, useEditDistrictMutation } from "../../districtsApiSlice";
import PrimaryDistrictInfoSection from "./PrimaryDistrictInfoSection";
import DistrictAddressInfoSection from "./DistrictAddressInfoSection";

interface DistrictInfoSectionProps {
	districtInfo: DistrictCreateEditModel;
	returnTo?: string;
}

type DistrictInfoState = {
	name: string;
	code: string;
	externalID: string;
	addressContactInfo: {
		address: string;
		address2: string;
		city: string;
		zoneId?: number;
		countryId?: number;
		zip: string;
		contactName: string;
		emailAddress: string;
		phone: string;
	};
	cleverClassLinkInfo: {
		cleverId: string;
		showCleverId: boolean;
		classLinkId: string;
		showClassLinkId: boolean;
	};
};

export default function DistrictInfoSection(props: DistrictInfoSectionProps) {
	const navigate = useNavigate();
	const { config } = useAppSelector((state) => state.site);

	const [saveError, setSaveError] = useState("");
	const [disableSave, setDisableSave] = useState(false);

	const [districtInfoState, setDistrictInfoState] = useState<DistrictInfoState>({
		name: props.districtInfo.name,
		code: props.districtInfo.code,
		externalID: props.districtInfo.externalID,
		addressContactInfo: {
			address: props.districtInfo.address,
			address2: props.districtInfo.address2,
			city: props.districtInfo.city,
			zoneId: props.districtInfo.zoneId,
			countryId: props.districtInfo.countryId,
			zip: props.districtInfo.zip,
			contactName: props.districtInfo.contactName,
			emailAddress: props.districtInfo.emailAddress,
			phone: props.districtInfo.phone,
		},
		cleverClassLinkInfo: {
			cleverId: props.districtInfo.cleverID,
			showCleverId: props.districtInfo.showCleverId,
			classLinkId: props.districtInfo.classLinkID,
			showClassLinkId: props.districtInfo.showClassLinkId,
		},
	});

	const setPrimaryInfo = (
		patch: Partial<Pick<DistrictInfoState, "name" | "code" | "externalID">>
	) => setDistrictInfoState((s) => ({ ...s, ...patch }));

	const setAddressInfo = (patch: Partial<DistrictInfoState["addressContactInfo"]>) =>
		setDistrictInfoState((s) => ({
			...s,
			addressContactInfo: { ...s.addressContactInfo, ...patch },
		}));

	const setCleverClassLinkInfo = (patch: Partial<DistrictInfoState["cleverClassLinkInfo"]>) =>
		setDistrictInfoState((s) => ({
			...s,
			cleverClassLinkInfo: { ...s.cleverClassLinkInfo, ...patch },
		}));

	const [showAddressSection, setShowAddressSection] = useState(false);

	function toggleShowAddressSection() {
		setShowAddressSection((val) => !val);
	}

	const [editDistrict] = useEditDistrictMutation();

	useEffect(() => {
		// Name and code are not-null in the database, so every district has them.  The rest only
		// became required later, so a district that was created without one may keep it empty.
		const { addressContactInfo } = districtInfoState;
		const hasEmptyRequiredFields =
			!districtInfoState.name ||
			!districtInfoState.code ||
			isMissingRequiredValue(districtInfoState.externalID, props.districtInfo.externalID) ||
			isMissingRequiredValue(addressContactInfo.city, props.districtInfo.city) ||
			isMissingRequiredValue(addressContactInfo.zoneId, props.districtInfo.zoneId) ||
			isMissingRequiredValue(addressContactInfo.countryId, props.districtInfo.countryId) ||
			isMissingRequiredValue(addressContactInfo.contactName, props.districtInfo.contactName);
		// Compare the fields the form owns, one by one, so that undoing an edit puts the form
		// back to unchanged.  The country is left out on purpose: it is defaulted for display
		// and nothing stores it on its own -- only the state persists, and it carries its own
		// country with it.
		const hasChanges =
			textChanged(districtInfoState.name, props.districtInfo.name) ||
			textChanged(districtInfoState.code, props.districtInfo.code) ||
			textChanged(districtInfoState.externalID, props.districtInfo.externalID) ||
			textChanged(addressContactInfo.address, props.districtInfo.address) ||
			textChanged(addressContactInfo.address2, props.districtInfo.address2) ||
			textChanged(addressContactInfo.city, props.districtInfo.city) ||
			textChanged(addressContactInfo.zip, props.districtInfo.zip) ||
			textChanged(addressContactInfo.contactName, props.districtInfo.contactName) ||
			textChanged(addressContactInfo.emailAddress, props.districtInfo.emailAddress) ||
			textChanged(addressContactInfo.phone, props.districtInfo.phone) ||
			selectionChanged(addressContactInfo.zoneId, props.districtInfo.zoneId);

		if (hasEmptyRequiredFields || !hasChanges) {
			setDisableSave(true);
		} else {
			setDisableSave(false);
		}
	}, [districtInfoState]);

	function updateModel(): DistrictCreateEditModel {
		const model: DistrictCreateEditModel = {
			...props.districtInfo,
			name: districtInfoState.name,
			code: districtInfoState.code,
			externalID: districtInfoState.externalID,
			address: districtInfoState.addressContactInfo.address,
			address2: districtInfoState.addressContactInfo.address2,
			city: districtInfoState.addressContactInfo.city,
			zoneId: districtInfoState.addressContactInfo.zoneId,
			countryId: districtInfoState.addressContactInfo.countryId,
			zip: districtInfoState.addressContactInfo.zip,
			contactName: districtInfoState.addressContactInfo.contactName,
			emailAddress: districtInfoState.addressContactInfo.emailAddress,
			phone: districtInfoState.addressContactInfo.phone,
		};
		return model;
	}

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = updateModel();

		if (model) {
			editDistrict(model)
				.unwrap()
				.then(() => {
					navigate(props.returnTo ?? `/districts`);
				})
				.catch((error) => {
					setSaveError(error.data);
					scrollTo(0, 0);
					console.log(error);
				});
		}
	}

	function handleCancel() {
		navigate(props.returnTo ?? `/districts`);
	}

	return (
		<section>
			{!config?.isUtahComposeSite && <hr className="my-6" />}
			<AlertMessage
				className="mb-2"
				type={AlertLevel.Error}
				message={saveError}
				showIcon={true}
				onClear={() => setSaveError("")}
			/>
			<PrimaryDistrictInfoSection
				value={{
					districtId: props.districtInfo.id,
					name: districtInfoState.name,
					code: districtInfoState.code,
					externalID: districtInfoState.externalID,
					cleverId: districtInfoState.cleverClassLinkInfo.cleverId,
					showCleverId: districtInfoState.cleverClassLinkInfo.showCleverId,
					classLinkId: districtInfoState.cleverClassLinkInfo.classLinkId,
					showClassLinkId: districtInfoState.cleverClassLinkInfo.showClassLinkId,
				}}
				onPrimaryInfoChange={setPrimaryInfo}
				onCleverClassLinkChange={setCleverClassLinkInfo}
				editMode
			/>
			<DistrictAddressInfoSection
				value={districtInfoState.addressContactInfo}
				onAddressChange={setAddressInfo}
				editMode
				showAddressSection={showAddressSection}
				toggleShowAddressSection={toggleShowAddressSection}
			/>
			<div className="flex items-center justify-end gap-x-4">
				<Button type="button" className="btn btn-naked" onClick={handleCancel}>
					Cancel
				</Button>
				<Button
					type="submit"
					className="btn btn-primary"
					disabled={disableSave}
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
					Save
				</Button>
			</div>
		</section>
	);
}
